/**
 * DeliveryAdjustmentPanel
 * ─────────────────────────────────────────────────────────────────────────
 * Dispatch-time delivery fee adjustments for staff. The agent picks a CODE
 * and supplies the facts (minutes waited, floors, the new pin, extra
 * vehicles); the server computes the money from the configured rates and
 * the order's own quote. There is deliberately no amount field.
 *
 * Endpoints: GET /weborders/delivery-adjustments/catalogue,
 *            GET/POST /weborders/:orderId/delivery-adjustments,
 *            POST /weborders/:orderId/delivery-adjustments/:id/waive
 */
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { dashboardApi } from "../api/index.js";
import AppSelect from "./ui/AppSelect";

const money = (amount, currency = "UGX") => `${currency} ${Number(amount || 0).toLocaleString()}`;
const dateTime = (value) => (value ? new Date(value).toLocaleString() : "");

const STATUS_VARIANT = {
  APPLIED: "success",
  PENDING_CUSTOMER: "warning",
  DECLINED: "danger",
  WAIVED: "secondary",
};

const PARAM_FIELDS = {
  EXTRA_VEHICLE: [{ key: "vehicles", label: "Extra vehicles", type: "number", min: 1, placeholder: "1" }],
  UPGRADE_VEHICLE: [
    { key: "toClass", label: "Vehicle needed", type: "select", options: ["CAR", "VAN", "TRUCK"] },
  ],
  LOCATION_CHANGE: [
    { key: "lat", label: "New drop-off latitude", type: "number", step: "any", placeholder: "0.3476" },
    { key: "lng", label: "New drop-off longitude", type: "number", step: "any", placeholder: "32.5825" },
    { key: "address", label: "New address (optional)", type: "text" },
  ],
  WAITING_TIME: [{ key: "minutes", label: "Minutes waited", type: "number", min: 0, placeholder: "30" }],
  RETURN_TRIP: [],
  TEAM_LIFT: [],
  STAIRS: [{ key: "floors", label: "Floors carried up", type: "number", min: 1, placeholder: "2" }],
};

function buildParams(code, form) {
  if (code === "LOCATION_CHANGE") {
    return {
      dropoffCoords: { lat: Number(form.lat), lng: Number(form.lng) },
      ...(form.address ? { address: form.address } : {}),
    };
  }
  const out = {};
  for (const field of PARAM_FIELDS[code] || []) {
    if (form[field.key] !== undefined && form[field.key] !== "") {
      out[field.key] = field.type === "number" ? Number(form[field.key]) : form[field.key];
    }
  }
  return out;
}

export default function DeliveryAdjustmentPanel({ orderId, onChanged }) {
  const [catalogue, setCatalogue] = useState([]);
  const [rates, setRates] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [code, setCode] = useState("");
  const [form, setForm] = useState({});
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      const [cat, ord] = await Promise.all([
        dashboardApi.get("/weborders/delivery-adjustments/catalogue"),
        dashboardApi.get(`/weborders/${orderId}/delivery-adjustments`),
      ]);
      const entries = cat.data?.catalogue || [];
      setCatalogue(entries.filter((e) => e.code !== "_rates"));
      setRates(entries.find((e) => e.code === "_rates")?.params || null);
      setOrder(ord.data?.order || null);
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not load delivery adjustments.");
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (event) => {
    event.preventDefault();
    if (!code) return;
    setSaving(true);
    try {
      const { data } = await dashboardApi.post(`/weborders/${orderId}/delivery-adjustments`, {
        code,
        params: buildParams(code, form),
        note,
      });
      const adj = data.adjustment;
      toast.success(
        adj.status === "PENDING_CUSTOMER"
          ? `Sent to the customer for approval: ${money(adj.amount, adj.currency)}`
          : `Applied: ${money(adj.amount, adj.currency)}`
      );
      setCode("");
      setForm({});
      setNote("");
      await load();
      if (typeof onChanged === "function") onChanged(data.order);
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not apply the adjustment.");
    } finally {
      setSaving(false);
    }
  };

  const waive = async (adjustmentId) => {
    setSaving(true);
    try {
      await dashboardApi.post(`/weborders/${orderId}/delivery-adjustments/${adjustmentId}/waive`);
      toast.success("Adjustment waived.");
      await load();
      if (typeof onChanged === "function") onChanged();
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not waive the adjustment.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center" style={{ gap: 8 }}>
        <Spinner animation="border" size="sm" /> Loading delivery adjustments
      </div>
    );
  }
  if (!order) return null;

  const fields = PARAM_FIELDS[code] || [];
  const applied = order.deliveryAdjustments || [];

  return (
    <div>
      <Row className="mb-2">
        <Col md={3}><small className="text-muted">Checkout fee</small><div>{money(order.deliveryFeeBase ?? order.deliveryFee)}</div></Col>
        <Col md={3}><small className="text-muted">Adjustments</small><div>{money(order.deliveryAdjustmentsTotal)}</div></Col>
        <Col md={3}><small className="text-muted">Delivery fee now</small><div><strong>{money(order.deliveryFee)}</strong></div></Col>
        <Col md={3}><small className="text-muted">Vehicle class</small><div>{order.deliveryLoadClass || "MOTORCYCLE"}</div></Col>
      </Row>
      {Number(order.balanceDue) > 0 && (
        <Alert variant="warning" className="py-2">
          Balance due at the door: {money(order.balanceDue)} (accepted after payment).
        </Alert>
      )}

      {applied.length > 0 && (
        <Table size="sm" responsive className="mb-3">
          <thead>
            <tr><th>When</th><th>What</th><th>Amount</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {applied.map((a) => (
              <tr key={a._id}>
                <td>{dateTime(a.requestedAt)}</td>
                <td>{a.description || a.code}{a.note ? <div className="text-muted small">{a.note}</div> : null}</td>
                <td>{money(a.amount, a.currency)}</td>
                <td><Badge variant={STATUS_VARIANT[a.status] || "secondary"}>{a.status.replace("_", " ")}</Badge></td>
                <td>
                  {a.status === "PENDING_CUSTOMER" && (
                    <Button size="sm" variant="outline-secondary" disabled={saving} onClick={() => waive(a._id)}>
                      Waive
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Form onSubmit={submit}>
        <Row>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Adjustment</Form.Label>
              <AppSelect block value={code} onChange={(e) => { setCode(e.target.value); setForm({}); }}>
                <option value="">Choose a reason</option>
                {catalogue.map((c) => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </AppSelect>
            </Form.Group>
          </Col>
          {fields.map((f) => (
            <Col md={f.type === "text" ? 4 : 2} key={f.key}>
              <Form.Group>
                <Form.Label>{f.label}</Form.Label>
                {f.type === "select" ? (
                  <AppSelect block value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}>
                    <option value="">Select</option>
                    {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </AppSelect>
                ) : (
                  <Form.Control
                    type={f.type}
                    min={f.min}
                    step={f.step}
                    placeholder={f.placeholder}
                    value={form[f.key] || ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  />
                )}
              </Form.Group>
            </Col>
          ))}
        </Row>
        <Row>
          <Col md={8}>
            <Form.Group>
              <Form.Label>Note for the record (optional)</Form.Label>
              <Form.Control value={note} onChange={(e) => setNote(e.target.value)} placeholder="What happened" />
            </Form.Group>
          </Col>
          <Col md={4} className="d-flex align-items-end">
            <Button type="submit" disabled={!code || saving}>
              {saving ? "Applying" : "Apply adjustment"}
            </Button>
          </Col>
        </Row>
        <Form.Text className="text-muted">
          The amount is computed by the server from the configured rates
          {rates ? ` (waiting ${money(rates.waitingPerBlock)} per ${rates.waitingBlockMinutes} min after ${rates.freeWaitingMinutes} free, return trip ${rates.returnTripPct}% of the fee, team lift ${money(rates.teamLiftFee)}, stairs ${money(rates.stairsPerFloor)} per floor)` : ""}.
          A paid order is asked to approve the change in the app first.
        </Form.Text>
      </Form>
    </div>
  );
}
